import React, { useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { SERVED_NAMES, COUNTRIES } from "../data/footprint";

const TOPO = "/assets/maps/world-110m.json";

// We project on Africa only by constraining the map view.
// Africa bbox roughly: lon [-20, 55], lat [-37, 38]. We use projection geoAzimuthalEqualArea around Africa center.
const PROJECTION_CONFIG = {
  scale: 360,
  center: [18, 5],
};

export default function AfricaMap({ onSelect }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div className="relative" data-testid="africa-map">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={PROJECTION_CONFIG}
        width={780}
        height={780}
        style={{ width: "100%", height: "auto" }}
      >
        <Geographies geography={TOPO}>
          {({ geographies }) =>
            geographies
              .filter((geo) => {
                // Keep only the African continent by name-based filter (continent metadata is not present
                // in world-110m, so we approximate by latitude or by an explicit allow-list).
                const props = geo.properties || {};
                return AFRICA_NAMES.has(props.name);
              })
              .map((geo) => {
                const name = geo.properties?.name;
                const served = SERVED_NAMES.has(name);
                const isHovered = hovered === name;
                const country = COUNTRIES.find((c) => c.name === name);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => setHovered(name)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => served && onSelect && onSelect(country)}
                    style={{
                      default: {
                        fill: served ? "#E60000" : "#1a1a1a",
                        stroke: "#000",
                        strokeWidth: 0.4,
                        outline: "none",
                        opacity: served ? 0.85 : 0.7,
                      },
                      hover: {
                        fill: served ? "#FF1A1A" : "#262626",
                        outline: "none",
                        cursor: served ? "pointer" : "default",
                        opacity: 1,
                      },
                      pressed: { fill: "#FF1A1A", outline: "none" },
                    }}
                    data-testid={served ? `country-${country?.code?.toLowerCase()}` : undefined}
                  />
                );
              })
          }
        </Geographies>
      </ComposableMap>

      {hovered && (
        <div
          className="pointer-events-none absolute left-4 top-4 border border-white/15 bg-black/85 px-3 py-2 backdrop-blur-md"
          data-testid="map-tooltip"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-textDim">Country</div>
          <div className="mt-1 flex items-center gap-2 font-heading text-base text-white">
            {COUNTRIES.find((c) => c.name === hovered)?.flag || ""}
            {hovered}
          </div>
          {SERVED_NAMES.has(hovered) && (
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-red">
              Programs delivered
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Allow-list of African country names as they appear in world-atlas 110m
const AFRICA_NAMES = new Set([
  "Algeria","Angola","Benin","Botswana","Burkina Faso","Burundi","Cameroon","Central African Rep.","Chad","Comoros",
  "Congo","Côte d'Ivoire","Dem. Rep. Congo","Djibouti","Egypt","Eq. Guinea","Eritrea","eSwatini","Ethiopia","Gabon",
  "Gambia","Ghana","Guinea","Guinea-Bissau","Kenya","Lesotho","Liberia","Libya","Madagascar","Malawi","Mali",
  "Mauritania","Mauritius","Morocco","Mozambique","Namibia","Niger","Nigeria","Rwanda","Senegal","Sierra Leone",
  "Somalia","Somaliland","South Africa","S. Sudan","Sudan","São Tomé and Principe","Tanzania","Togo","Tunisia",
  "Uganda","W. Sahara","Zambia","Zimbabwe",
]);
